import { useTranslation } from "react-i18next";
import {
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Button,
  Flex,
  Form,
  message,
  Spin,
  Typography,
  Upload,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useParams } from 'react-router-dom';
import { BASE_URL_V2 } from '../../../../../config/api/configApiv2';
import {
  createTeacherInstruct,
  getEnterpriseCourse,
  getTeacherCourse,
  updateImageTeacher,
} from '../../../../../service/content-course/contenCourseApi';
import { useAuthStore } from '../../../../../stores/stores';
import { ConstructCourseDetailType } from '../../../../../types/course-construction';
import { FileType } from '../../../../../utils/uploadImage';
import CustomCard from '../../../../custom-card/CustomCard';
import FormItemDropdown from '../../../../form-dropdown/FormItemDropdown';
import FormItemInput from '../../../../form-input/FormInput';
import './index.scss';

const { Text } = Typography;

export type TabInstructorProps = {
  dataInstructorTeacher: any[];
  dataConfig?: ConstructCourseDetailType;
  isDirty: boolean;
  setDirty: (v: boolean) => void;
  setReload: (v: boolean) => void;
  reload: boolean
};

const VALIDATION = {
  instructor: { required: true, message: 'Vui lòng chọn người hướng dẫn' },
  enterprise: { required: true, message: 'Vui lòng chọn trường' },
} as const;

const EMPTY_GROUP = '__ungrouped__';
const toPublicUrl = (path?: string) =>
  !path ? '' : path.startsWith('http') ? path : `${BASE_URL_V2}/cdn?path=${path}`;

const TabInstructor: React.FC<TabInstructorProps> = memo(
  ({ dataInstructorTeacher, setDirty, reload, setReload }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const accessToken = useAuthStore((s) => s.accessToken);

    const [enterpriseOptions, setEnterpriseOptions] = useState<any[]>([]);
    const [teacherOptions, setTeacherOptions] = useState<Record<string, any[]>>({});
    const [loadingUpload, setLoadingUpload] = useState<Record<number, boolean>>({});
    const [removedIds, setRemovedIds] = useState<number[]>([]);

    const [form] = Form.useForm();
    const instructors: any[] = Form.useWatch('instructors', form) ?? [];

    useEffect(() => {
      if (!id) return;
      getEnterpriseCourse(Number(id))
        .then(({ data }) => setEnterpriseOptions(data.data ?? []))
        .catch(console.error);
    }, [id]);

    useEffect(() => {
      const uuids = _.uniq(instructors.map((x) => x?.uuid).filter(Boolean));
      uuids.forEach((uuid) => {
        if (teacherOptions[uuid]) return;
        getTeacherCourse(uuid)
          .then(({ data }) =>
            setTeacherOptions((prev) => ({ ...prev, [uuid]: data.data ?? [] })),
          )
          .catch(console.error);
      });
    }, [instructors, teacherOptions]);

    const beforeUpload = useCallback((file: FileType) => {
      const ok = ['image/jpeg', 'image/png'].includes(file.type);
      if (!ok) message.error(t("File phải là JPG/PNG", "File phải là JPG/PNG"));
      if (file.size / 1024 / 1024 > 25) message.error('File ≤ 25 MB');
      return ok;
    }, []);

    const onUploadChange = useCallback(
      (info: any, idx: number) => {
        if (info.file.status === 'uploading') {
          setLoadingUpload((s) => ({ ...s, [idx]: true }));
          return;
        }
        if (info.file.status === 'done') {
          const path = info.file?.response?.filePath as string | undefined;

          if (!path) return;
          message.success(t("Tải ảnh thành công", "Tải ảnh thành công"));
          setLoadingUpload((s) => ({ ...s, [idx]: false }));
          form.setFieldValue(['instructors', idx, 'imageTeacher'], path);
        }
      },
      [form],
    );

    const groupFields = useCallback(
      (fields: any[]) =>
        fields.reduce<Record<string, any[]>>((acc, f) => {
          const hasId = !!form.getFieldValue(['instructors', f.name, 'id']);
          const uuidKey = hasId
            ? form.getFieldValue(['instructors', f.name, 'uuid']) || EMPTY_GROUP
            : EMPTY_GROUP;
          (acc[uuidKey] = acc[uuidKey] ?? []).push(f);
          return acc;
        }, {}),
      [form],
    );

    const enterpriseMap = useMemo(
      () => Object.fromEntries(enterpriseOptions.map((e) => [e.uuid, e.enterName])),
      [enterpriseOptions],
    );

    const buildAvatar = (field: any) => {
      const val = form.getFieldValue(['instructors', field.name, 'imageTeacher']);
      const src = typeof val === 'string' ? toPublicUrl(val) : '';
      return (
        <Upload
          action={`${BASE_URL_V2}/mooc-course-block-quiz/upload-file`}
          headers={{ Authorization: `Bearer ${accessToken}` }}
          listType="picture-card"
          showUploadList={false}
          beforeUpload={beforeUpload}
          onChange={(info) => onUploadChange(info, field.name)}
        >
          {loadingUpload[field.name] ? (
            <LoadingOutlined />
          ) : src ? (
            <img src={src} alt="avatar" style={{ width: '100%' }} />
          ) : (
            <div className="upload-content">
              <PlusOutlined /> <span>{t("Tải lên", "Tải lên")}</span>
            </div>
          )}
        </Upload>
      );
    };

    const computePayloads = (current: any[]) => {
      const addNew = current
        .filter((c) => !c.id)
        .map((i) => ({
          teacherId: i.teacherId,
          uuid: i.uuid,
          imageTeacher: getFileName(i.imageTeacher),
          biography: i.biography,
        }));

      const update = current
        .filter((c) => c.id)
        .map((i) => ({
          id: i.id,
          teacherId: i.teacherId,
          uuid: i.uuid,
          imageTeacher: getFileName(i.imageTeacher),
          biography: i.biography,
        }));

      return { addNew, update, deleted: removedIds };
    };

    const getFileName = (p?: string) => {
      if (!p) return '';
      const part = p.includes('?path=') ? p.split('?path=').pop()! : p;
      return part;
    };

    return (
      <>
        {reload ? <div className='section-instructor-reload'>
          <Spin />
        </div> :
          <div className="section-instructor">
            <Form
              form={form}
              initialValues={{ instructors: dataInstructorTeacher }}
              onFinish={async (vals) => {
                setDirty(false);
                const list = vals.instructors as any[];

                const imgPayload = list
                  .filter((i) => i.imageTeacher && typeof i.imageTeacher === 'string')
                  .map((i) => ({
                    teacherId: i.teacherId,
                    imageTeacher: getFileName(i.imageTeacher),
                  }));

                if (imgPayload.length) {
                  await updateImageTeacher({
                    courseId: Number(id),
                    courseTeacherUpdateRequest: imgPayload,
                  });
                }

                const { addNew, update, deleted } = computePayloads(list);
                await createTeacherInstruct({
                  courseId: Number(id),
                  courseTeacherAddNewRequest: addNew,
                  courseTeacherUpdateRequest: update,
                  teacherDelete: deleted,
                });
                setReload(!reload)
                message.success(t("Lưu cấu hình thành công", "Lưu cấu hình thành công"));
              }}
              onValuesChange={() => setDirty(true)}
              layout="vertical"
            >
              <Form.List name="instructors">
                {(fields, { add, remove }) => {
                  const addRow = () => {
                    add({});
                    setDirty(true);
                  };

                  const removeRow = (field: any) => {
                    const rowVal = form.getFieldValue(['instructors', field.name]);
                    if (rowVal?.id) setRemovedIds((prev) => [...prev, rowVal.id]);
                    remove(field.name);
                    setDirty(true);
                  };

                  const renderRow = (field: any) => {
                    const rowVal = form.getFieldValue(['instructors', field.name]);
                    const isExisting = !!rowVal?.id;
                    const rowUuid = rowVal?.uuid;
                    const teachersByUni = teacherOptions[rowUuid] ?? [];

                    const selectedIds = instructors.map((i) => i.teacherId).filter(Boolean);
                    const teacherOpts = teachersByUni.map((t: any) => ({
                      name: t.nameUser,
                      code: t.userId,
                      disabled: selectedIds.includes(t.userId),
                    }));

                    return (
                      <div className="instructor" key={field.key}>
                        <Flex gap={24} align="flex-start" className=' mt-3'>
                          <div className="flex-1">
                            {isExisting ? (
                              null
                            ) : (
                              <Form.Item
                                label={t("Trường", "Trường")}
                                name={[field.name, 'uuid']}
                                rules={[VALIDATION.enterprise]}
                              >
                                <FormItemDropdown
                                  options={enterpriseOptions.map((e) => ({
                                    name: e.enterName,
                                    code: e.uuid,
                                  }))}
                                />
                              </Form.Item>
                            )}

                            <Form.Item
                              label={t("Người hướng dẫn", "Người hướng dẫn")}
                              name={[field.name, 'teacherId']}
                              rules={[VALIDATION.instructor]}
                            >
                              <FormItemDropdown
                                placeholder={t("Chọn người hướng dẫn", "Chọn người hướng dẫn")}
                                options={teacherOpts}
                                onChange={(val) => {
                                  const found = teachersByUni.find(
                                    (t: any) => t.userId === val,
                                  );
                                  if (found) {
                                    form.setFields([
                                      {
                                        name: ['instructors', field.name, 'teacherName'],
                                        value: found.nameUser,
                                      },
                                      {
                                        name: ['instructors', field.name, 'email'],
                                        value: found.userEmail,
                                      },
                                    ]);
                                  }
                                }}
                                disabled={isExisting}
                              />
                            </Form.Item>

                            <div className="flex align-center gap-24">
                              <div className="flex-1">
                                <Form.Item label="Email" name={[field.name, 'email']}>
                                  <FormItemInput disabled />
                                </Form.Item>
                                <Form.Item
                                  label={t("Tiểu sử", "Tiểu sử")}
                                  name={[field.name, 'biography']}
                                >
                                  <TextArea rows={2} placeholder={t("Nhập tiểu sử", "Nhập tiểu sử")} />
                                </Form.Item>
                              </div>

                              <Form.Item name={[field.name, 'imageTeacher']}>
                                <div className="image-container">
                                  {buildAvatar(field)}
                                  {rowVal?.imageTeacher && (
                                    <span className="remove-icon">
                                      <a
                                        onClick={() =>
                                          form.setFieldValue(
                                            ['instructors', field.name, 'imageTeacher'],
                                            null,
                                          )
                                        }
                                        className='m-auto'
                                      >
                                        <DeleteOutlined />
                                      </a>
                                    </span>
                                  )}
                                </div>
                              </Form.Item>
                            </div>
                          </div>

                          <CloseOutlined
                            style={{ marginTop: 8 }}
                            onClick={() => removeRow(field)}
                          />
                        </Flex>
                        <div className="hr-tag"></div>
                      </div>
                    );
                  };

                  const groups = groupFields(fields);

                  return (
                    <>
                      {Object.entries(groups).map(([uuid, group]) => (
                        <CustomCard
                          key={uuid}
                          title={enterpriseMap[uuid] || t("Chưa chọn trường", "Chưa chọn trường")}
                          className="section-instructor--card"
                        >
                          {group.map(renderRow)}
                        </CustomCard>
                      ))}

                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        style={{ marginTop: 24 }}
                        onClick={addRow}
                      >
                        {t("Thêm người hướng dẫn", "Thêm người hướng dẫn")}
                      </Button>
                    </>
                  );
                }}
              </Form.List>

              <div className="flex gap-16 mt-4">
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  {t("Lưu", "Lưu")}
                </Button>
                <Button icon={<CloseOutlined />} onClick={() => form.resetFields()}>
                  {t("Hủy", "Hủy")}
                </Button>
              </div>
            </Form>
          </div>}
      </>
    );
  },
);

export default TabInstructor;
